import _ from 'lodash';
import QuillBlock, { bubbleFormats } from 'quill/blots/block';
import Quill from 'quill/core';
import { useEffect, useRef, useState } from 'react';
import type QuillEditor from './QuillEditor';
import type { Blot, Boundary, Range, TextBlot } from './types';

const Parchment = Quill.import('parchment');
const Module = Quill.import('core/module');

const EVENTS = {
  CHANGE: 'prefix-listener-change',
  RESET: 'prefix-listener-reset',
};

const allowedPrecedingCharRegex = /[\s{[("<]/;
export function isPrecedingCharAllowed(char: string | undefined) {
  return char ? allowedPrecedingCharRegex.test(char) : true;
}

function getPrecedingCharFromPreviousBlot(
  quill: Quill,
  range: Range,
  blot: Blot | null,
) {
  const [prevLeaf, prevLeafText] = getLeafAndPrefixText(quill, {
    ...range,
    index: range.index - 1,
  });
  return prevLeafText && inSameLine(blot, prevLeaf)
    ? prevLeafText[prevLeafText.length - 1]
    : '';
}

function getLeafAndPrefixText(
  quill: Quill,
  range: Range,
): [TextBlot, string] | [null, ''] {
  const [leaf, offset] = quill.getLeaf(range.index);
  if (leaf instanceof Parchment.TextBlot) {
    return [leaf, leaf.value().slice(0, offset)];
  }

  return [null, ''];
}

function getClosestLine(blot: Blot | null) {
  let current = blot;
  while (current && !(current instanceof QuillBlock)) {
    current = current.parent;
  }
  return current;
}

function inSameLine(blot1: Blot | null, blot2: Blot | null) {
  const line = getClosestLine(blot1);
  return line ? line === getClosestLine(blot2) : false;
}

type Setting = { prefix: string; isAllowedInTitle: boolean };
type State = {
  prefix: string;
  text: string;
  blot: Blot;
  bounds: Boundary;
  isInTitle: boolean;
  range: Range;
};

const quillToSettings = new WeakMap<Quill, Setting[]>();

type PrefixListenerOptions = {
  persistOnSelectionChange?: boolean;
};

class PrefixListener extends Module<PrefixListenerOptions> {
  // @ts-expect-error Use `declare quill: QuillEditor` in Babel 8
  quill: QuillEditor;

  private activeState: State | null = null;

  constructor(quill: QuillEditor, options: PrefixListenerOptions) {
    super(quill, options);

    this.setupListener();
  }

  private get settings() {
    return quillToSettings.get(this.quill) || [];
  }

  private setActiveState(state: State | null) {
    if (this.activeState !== state) {
      this.activeState = state;
      this.quill.emitter.emit(EVENTS.CHANGE, state);
      if (this.activeState) {
        this.quill.root.setAttribute('autocorrect', 'off');
      } else {
        this.quill.root.removeAttribute('autocorrect');
      }
    }
  }

  private setupListener() {
    const { quill } = this;

    quill.on(Quill.events.TEXT_CHANGE, (_delta, _old, source) => {
      if (source !== Quill.sources.USER) return;
      _.defer(() => {
        quill.update(Quill.sources.SILENT); // Make sure to get range in consistent state
        const [range] = quill.selection.getRange();
        if (!range) {
          this.setActiveState(null);
          return;
        }

        const [leaf, prefixText] = getLeafAndPrefixText(quill, range);

        if (this.activeState) {
          const lastIndex = prefixText.lastIndexOf(this.activeState.prefix);
          if (lastIndex === -1) {
            this.setActiveState(null);
          } else {
            const distance = range.index - this.activeState.range.index;
            const canReusePrefixRange =
              distance > 0 && prefixText.length >= distance;

            const index = canReusePrefixRange
              ? prefixText.length - distance
              : lastIndex;
            this.setActiveState({
              ...this.activeState,
              text: prefixText.slice(index + this.activeState.prefix.length),
            });
          }

          return;
        }

        const setting = this.settings.find(({ prefix }) =>
          prefixText.endsWith(prefix),
        );

        if (!setting) return;

        if (bubbleFormats(leaf).code) return;

        const precedingChar =
          prefixText.length > 1
            ? prefixText[prefixText.length - setting.prefix.length - 1]
            : getPrecedingCharFromPreviousBlot(quill, range, leaf);

        if (!isPrecedingCharAllowed(precedingChar)) {
          return;
        }

        const [line] = quill.getLine(range.index);
        const isInTitle = quill.titleLine === line;
        const hasMatch = !isInTitle || setting.isAllowedInTitle;

        if (hasMatch) {
          const bounds = quill.getBounds(range.index);
          this.setActiveState({
            blot: line,
            bounds,
            prefix: setting.prefix,
            text: '',
            isInTitle,
            range: {
              index: range.index - setting.prefix.length,
              length: setting.prefix.length,
            },
          });
        }
      });
    });

    if (!this.options.persistOnSelectionChange) {
      quill.on(Quill.events.SELECTION_CHANGE, (_range, _old, source) => {
        if (source === Quill.sources.USER && this.activeState) {
          _.defer(() => {
            this.setActiveState(null);
          });
        }
      });
    }

    quill.on(EVENTS.RESET, () => {
      this.setActiveState(null);
    });
  }
}

type UsePrefixListenerOptions = (Setting | string)[];

const EMPTY_STATE = {
  bounds: undefined,
  text: undefined,
  prefix: undefined,
  reset: undefined,
  range: undefined,
  blot: undefined,
};

export const usePrefixListener = (
  quill: Quill | null,
  options: UsePrefixListenerOptions,
) => {
  const settingsRef = useRef<Setting[]>([]);
  const [state, setState] = useState<
    (State & { reset: () => void }) | typeof EMPTY_STATE
  >(EMPTY_STATE);

  useEffect(() => {
    if (!quill) return undefined;
    const settings = options.map((option) =>
      typeof option === 'string'
        ? { prefix: option, isAllowedInTitle: false }
        : option,
    );
    settingsRef.current = settings;

    quillToSettings.set(quill, [
      ...(quillToSettings.get(quill) || []),
      ...settings,
    ]);
    return () => {
      const allSettings = quillToSettings.get(quill);
      if (quillToSettings.has(quill)) {
        quillToSettings.set(quill, _.without(allSettings, ...settings));
      }
    };
  }, [quill, options]);

  useEffect(() => {
    if (!quill) return undefined;

    const reset = () => {
      quill.emitter.emit(EVENTS.RESET);
    };

    const listener = (prefixState: State | null) => {
      const isTarget =
        prefixState &&
        settingsRef.current.some(
          (setting) =>
            setting.prefix === prefixState.prefix &&
            (!prefixState.isInTitle || setting.isAllowedInTitle),
        );

      setState(isTarget ? { ...prefixState, reset } : EMPTY_STATE);
    };

    quill.on(EVENTS.CHANGE, listener);
    return () => {
      quill.off(EVENTS.CHANGE, listener);
    };
  }, [quill]);

  return state;
};

export default PrefixListener;
